/**
 * Local identity — protocol v2.
 *
 * Two Ed25519 keypairs per device:
 *   - identity key: long-term. Derives userId, signs bindings at
 *     /auth/claim and /auth/rotate-auth. Never signs a daily
 *     /auth/verify challenge.
 *   - auth key: rotatable. Signs /auth/verify challenges. If an
 *     attacker extracts just the auth key, the user rotates via
 *     /auth/rotate-auth without re-going through admin approval.
 *
 * Both keys are persisted in the KeyStore (sealed under a passphrase
 * via SealedKeyStore — see HIGH-06 in docs/security-audit.md) and
 * never leave the device.
 *
 * Storage key: `chat:identity:v2`. There was a plaintext v1 record in
 * early development before HIGH-06; that migration branch is now
 * gone (LOW-20 in docs/security-audit-v2.md). Users with a v1 record
 * in an unsealed IndexedDB can't be auto-migrated because the salt
 * never existed for them — they re-register via the admin flow.
 */
import { toBase64Url, type DeviceId, type UserId } from '@chat/protocol';
import type { KeyStore } from '@chat/crypto';
import { ed25519 } from '@noble/curves/ed25519.js';

const IDENTITY_KEY = 'chat:identity:v2';

export interface Identity {
  readonly userId: UserId;
  readonly deviceId: DeviceId;
  readonly identityPublicKey: Uint8Array;
  readonly identityPrivateKey: Uint8Array;
  readonly authPublicKey: Uint8Array;
  readonly authPrivateKey: Uint8Array;
}

interface StoredIdentityV2 {
  readonly version: 2;
  readonly identityPrivateKey: string;
  readonly identityPublicKey: string;
  readonly authPrivateKey: string;
  readonly authPublicKey: string;
  readonly userId: string;
  readonly deviceId: string;
}

function randomDeviceSuffix(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
}

export function createIdentity(): Identity {
  const identityPrivateKey = ed25519.utils.randomSecretKey();
  const identityPublicKey = ed25519.getPublicKey(identityPrivateKey);
  const authPrivateKey = ed25519.utils.randomSecretKey();
  const authPublicKey = ed25519.getPublicKey(authPrivateKey);
  const userId = `user-${toBase64Url(identityPublicKey)}` as UserId;
  const deviceId = `device-${randomDeviceSuffix()}${randomDeviceSuffix()}` as DeviceId;
  return {
    userId,
    deviceId,
    identityPublicKey,
    identityPrivateKey,
    authPublicKey,
    authPrivateKey,
  };
}

export async function saveIdentity(store: KeyStore, identity: Identity): Promise<void> {
  const stored: StoredIdentityV2 = {
    version: 2,
    identityPrivateKey: toBase64Url(identity.identityPrivateKey),
    identityPublicKey: toBase64Url(identity.identityPublicKey),
    authPrivateKey: toBase64Url(identity.authPrivateKey),
    authPublicKey: toBase64Url(identity.authPublicKey),
    userId: identity.userId,
    deviceId: identity.deviceId,
  };
  const bytes = new TextEncoder().encode(JSON.stringify(stored));
  await store.put(IDENTITY_KEY, bytes);
}

export async function loadIdentity(store: KeyStore): Promise<Identity | undefined> {
  const { fromBase64Url } = await import('@chat/protocol');
  const v2Bytes = await store.get(IDENTITY_KEY);
  if (!v2Bytes) return undefined;
  const stored = JSON.parse(new TextDecoder().decode(v2Bytes)) as StoredIdentityV2;
  return {
    userId: stored.userId as UserId,
    deviceId: stored.deviceId as DeviceId,
    identityPrivateKey: fromBase64Url(stored.identityPrivateKey),
    identityPublicKey: fromBase64Url(stored.identityPublicKey),
    authPrivateKey: fromBase64Url(stored.authPrivateKey),
    authPublicKey: fromBase64Url(stored.authPublicKey),
  };
}
